import { z } from 'zod';
import { TpAssetInfo } from '../assets/interfaces';
import { TpDataContext } from '../interfaces';
import { MutationLedgerPayloadV1, MutationLedgerPayloadV1Schema } from '../mutation-ledger/schemas';
import { mapMutationLedgerToDTO } from '../mutation-ledger/serialization';
import {
  TpSelectionStoragePayloadV1,
  selectionStoragePayloadV1Schema,
} from '../selection/snapshot/selectionFilterSerialization.schemas';
import { mapReduxToSelectionDTO } from '../selection/snapshot/selectionFilterSerialization.serialize';
import { TpDashboardTemplateDTO, dashboardTemplateDTOSchema } from '../dashboard-template/schemas';
import { mapReduxToDashboardTemplateDTO } from '../dashboard-template/serialization';
import { TpDashboardDefinition } from '../../features/visual-analytics/dashboard/interfaces';

export const DASHBOARD_STATE_PAYLOAD_VERSION = 1;
export const VIEW_STATE_QUERY_PARAM = 'viewState';

// TERMINOLOGY FOR VIEW STATE
export const VIEW_STATE_TERMS = {
  singular: 'dashboard state',
  plural: 'dashboard states',
  cap: 'Dashboard state',
  capPlural: 'Dashboard states',
  title: 'Dashboard State',
  titlePlural: 'Dashboard States',
};

/** Version-one serialized wrapper containing the independent selection and mutation-ledger payloads. */
export interface DashboardStatePayloadV1 {
  version: 1;
  assetType: string;
  selection: TpSelectionStoragePayloadV1;
  mutationLedger: MutationLedgerPayloadV1;
  dashboardTemplate?: TpDashboardTemplateDTO;
}

/** Zod contract for validating a complete dashboard-state payload before application. */
export const dashboardStatePayloadV1Schema = z
  .object({
    version: z.literal(1),
    assetType: z.string(),
    selection: selectionStoragePayloadV1Schema,
    mutationLedger: MutationLedgerPayloadV1Schema,
    dashboardTemplate: dashboardTemplateDTOSchema.optional(),
  })
  .superRefine((payload, context) => {
    if (payload.assetType !== payload.selection.assetType) {
      context.addIssue({
        code: 'custom',
        message: `The ${VIEW_STATE_TERMS.singular} asset type does not match its selection payload.`,
      });
    }
    if (payload.assetType !== payload.mutationLedger.assetType) {
      context.addIssue({
        code: 'custom',
        message: `The ${VIEW_STATE_TERMS.singular} asset type does not match its mutation ledger.`,
      });
    }
    if (payload.dashboardTemplate && payload.assetType !== payload.dashboardTemplate.assetType) {
      context.addIssue({
        code: 'custom',
        message: `The ${VIEW_STATE_TERMS.singular} asset type does not match its dashboard template.`,
      });
    }
  });

/** Inferred runtime type produced by {@link dashboardStatePayloadV1Schema}. */
export type TpDashboardStatePayloadV1 = z.infer<typeof dashboardStatePayloadV1Schema>;

export function buildDashboardStatePayload(
  dataContext: TpDataContext,
  assetInfo: TpAssetInfo,
  dashboardDef?: TpDashboardDefinition,
): TpDashboardStatePayloadV1 {
  return {
    version: DASHBOARD_STATE_PAYLOAD_VERSION,
    assetType: assetInfo.type,
    selection: mapReduxToSelectionDTO(dataContext, assetInfo.type),
    mutationLedger: mapMutationLedgerToDTO(dataContext, assetInfo.type),
    dashboardTemplate: dashboardDef ? mapReduxToDashboardTemplateDTO(dataContext, dashboardDef, assetInfo) : undefined,
  };
}
